// src/views/StudentPortal/ExamDateSheet.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  Table,
  Row,
  Badge,
  Spinner,
  Collapse,
} from 'reactstrap';
import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import useVendorBranch from '../../utils/useVendorBranch';
import { toast } from 'react-toastify';

const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const normalize = (s) => (s || '').toString().trim();

const ExamDateSheet = () => {
  const { user } = useSelector((state) => state.authState);
  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // ✅ Weekend/MyResults pattern: ONLY vendor/branch
  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const canLoad = useMemo(() => {
    return conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });
  }, [
    hasMultiBranch,
    hasSingleBranch,
    isSuperAdmin,
    isAdmin,
    selectionData.vendor_id,
    selectionData.branch_id,
  ]);

  const fetchDateSheet = async ({ silent = false } = {}) => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      setLoading(true);

      // ✅ NO enrollmentId / NO semNo
      const res = await apiService.get(
        `api/BatchExamMaster/GetMyExamDateSheet?vendorId=${vendorId}&branchId=${branchId}`,
      );

      if (res?.status === true) {
        setRows(res?.data || []);
        if (!silent) toast.success(res?.message || 'Data fetched');
      } else {
        setRows([]);
        toast.error(res?.message || 'Failed');
      }
    } catch (e) {
      console.error(e);
      setRows([]);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Auto load when vendor/branch selected
  useEffect(() => {
    if (!canLoad) return;
    fetchDateSheet({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad, selectionData.vendor_id, selectionData.branch_id]);

  // --------- Grouping (Exam -> rows) ----------
  const grouped = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];

    const sorted = [...list].sort((a, b) => {
      const pa = normalize(a?.pattern_name).toLowerCase();
      const pb = normalize(b?.pattern_name).toLowerCase();
      if (pa < pb) return -1;
      if (pa > pb) return 1;

      const da = a?.exam_date ? new Date(a.exam_date).getTime() : 0;
      const db = b?.exam_date ? new Date(b.exam_date).getTime() : 0;
      if (da !== db) return da - db;

      const sa = normalize(a?.subject_name).toLowerCase();
      const sb = normalize(b?.subject_name).toLowerCase();
      if (sa < sb) return -1;
      if (sa > sb) return 1;

      return 0;
    });

    const map = new Map();
    for (const r of sorted) {
      const examKey =
        (r?.batch_exam_id ?? '') + '|' + normalize(r?.pattern_name);

      if (!map.has(examKey)) {
        map.set(examKey, {
          batch_exam_id: r?.batch_exam_id ?? null,
          pattern_name: r?.pattern_name ?? '',
          sem_no: r?.sem_no ?? null,
          is_published: !!r?.is_published,
          items: [],
        });
      }
      map.get(examKey).items.push(r);
    }

    return Array.from(map.values());
  }, [rows]);

  const totalSubjects = useMemo(() => (rows?.length ? rows.length : 0), [rows]);

  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='p-3 mb-0 d-flex justify-content-between align-items-center'>
          <div className='d-flex align-items-center'>
            <i className='bi bi-calendar2-check mr-2' />
            <span className='mr-2'>Exam DateSheet</span>

            <Badge color='info' pill className='ml-2'>
              {totalSubjects} Subjects
            </Badge>

            {loading ? (
              <Badge color='warning' pill className='ml-2'>
                Loading...
              </Badge>
            ) : null}
          </div>

          <div className='d-flex align-items-center' style={{ gap: 8 }}>
            <Button
              color='secondary'
              outline
              onClick={() => setShowFilters((s) => !s)}
              title='Show/Hide'
            >
              <i className='bi bi-funnel' /> {showFilters ? 'Hide' : 'Show'}
            </Button>

            <Button
              color='primary'
              onClick={() => fetchDateSheet()}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size='sm' className='mr-2' />
                  Refreshing
                </>
              ) : (
                <>
                  <i className='bi bi-arrow-repeat' /> Refresh
                </>
              )}
            </Button>
          </div>
        </CardTitle>

        <CardBody>
          <Collapse isOpen={showFilters}>
            <Row className='mb-2'>
              <div className='col-12 d-flex align-items-center justify-content-between'>
                <div className='text-muted' style={{ fontSize: 13 }}>
                  Select Vendor/Branch then click Load.
                </div>

                <div className='d-flex' style={{ gap: 8 }}>
                  <Button
                    color='success'
                    onClick={() => fetchDateSheet()}
                    disabled={loading}
                    title='Load Datesheet'
                  >
                    <i className='bi bi-search' /> Load
                  </Button>
                </div>
              </div>
            </Row>

            <hr className='mt-2' />
          </Collapse>

          {/* ------------ LIST ------------ */}
          {grouped.length === 0 ? (
            <div className='text-center p-4'>
              <div style={{ fontSize: 34 }}>🗓️</div>
              <div className='mt-2 font-weight-bold'>No data available</div>
              <div className='text-muted'>
                Select Vendor/Branch, then click Load/Refresh.
              </div>
            </div>
          ) : (
            grouped.map((g, idx) => (
              <Card key={`${g.batch_exam_id}-${idx}`} className='mb-3'>
                <CardBody className='p-3'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <div
                      className='d-flex align-items-center'
                      style={{ gap: 8 }}
                    >
                      <span
                        className='font-weight-bold'
                        style={{ fontSize: 16 }}
                      >
                        {g.pattern_name || 'Exam'}
                      </span>

                      {g.sem_no ? (
                        <Badge color='primary' pill>
                          Sem {g.sem_no}
                        </Badge>
                      ) : null}

                      <Badge
                        color={g.is_published ? 'success' : 'secondary'}
                        pill
                      >
                        {g.is_published ? 'Published' : 'Not Published'}
                      </Badge>
                    </div>

                    <div className='text-muted'>
                      Total: <b>{g.items?.length || 0}</b>
                    </div>
                  </div>

                  <div style={{ maxHeight: 420, overflow: 'auto' }}>
                    <Table striped hover responsive className='mb-0'>
                      <thead
                        style={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          background: '#fff',
                        }}
                      >
                        <tr>
                          <th style={{ width: 50 }}>#</th>
                          <th>Subject</th>
                          <th style={{ width: 140 }}>Exam Date</th>
                          <th style={{ width: 180 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.items?.map((x, i) => (
                          <tr key={`${x.batch_exam_id}-${x.subject_id}-${i}`}>
                            <td>{i + 1}</td>
                            <td>
                              <div className='font-weight-bold'>
                                {x.subject_name || ''}
                              </div>
                              <div
                                className='text-muted'
                                style={{ fontSize: 12 }}
                              >
                                Batch: {x.batch_name || '-'}{' '}
                                {x.batch_code ? `(${x.batch_code})` : ''}
                                {x.class_name
                                  ? ` • Class: ${x.class_name}`
                                  : ''}
                              </div>
                            </td>
                            <td>{fmtDate(x.exam_date)}</td>
                            <td>
                              {x.exam_conduct_status ? (
                                <Badge color='info' pill>
                                  {x.exam_conduct_status}
                                </Badge>
                              ) : (
                                <span className='text-muted'>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            ))
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default ExamDateSheet;
